import React, { useState } from 'react';
import { Upload, Download, X, AlertCircle, CheckCircle, FileText, Eye } from 'lucide-react';

/**
 * CSV Import Modal Component
 * Generic component for importing CSV data
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Close modal callback
 * @param {string} title - Modal title
 * @param {string} type - 'accounts' or 'investments'
 * @param {function} onDownloadTemplate - Download template callback
 * @param {function} onValidate - Validate CSV data callback
 * @param {function} onImport - Import data callback
 * @param {string} templateFilename - Template filename
 */
const CSVImportModal = ({
  isOpen,
  onClose,
  title,
  type,
  onDownloadTemplate,
  onValidate,
  onImport,
  templateFilename,
  instructions
}) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [validation, setValidation] = useState(null);
  const [step, setStep] = useState(1); // 1: upload, 2: preview, 3: importing
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  if (!isOpen) return null;

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }

    setFile(selectedFile);

    try {
      // Read and parse file
      const text = await readFile(selectedFile);
      const data = parseSimpleCSV(text);
      setParsedData(data);

      // Validate data
      const validationResult = await onValidate(data);
      setValidation(validationResult);

      // Move to preview step
      setStep(2);
    } catch (error) {
      console.error('Error reading file:', error);
      alert('Error reading file. Please try again.');
    }
  };

  const readFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  };

  const parseSimpleCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length === 0) return [];

    const headers = lines[0].split(',').map(h => h.trim());
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      data.push(row);
    }

    return data;
  };

  const handleImport = async () => {
    if (!validation?.valid) return;

    setImporting(true);
    setStep(3);

    try {
      const result = await onImport(parsedData);
      setImportResult(result);
    } catch (error) {
      console.error('Import error:', error);
      setImportResult({
        success: false,
        message: error.message || 'Import failed. Please try again.'
      });
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setParsedData(null);
    setValidation(null);
    setStep(1);
    setImporting(false);
    setImportResult(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900">{title}</h3>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Upload */}
          {step === 1 && (
            <div className="space-y-6">
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-2">How to import {type}:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Download the CSV template below</li>
                      <li>Fill in your {type} data (you can use Excel, Google Sheets, or any CSV editor)</li>
                      <li>Save the file as CSV</li>
                      <li>Upload the file here</li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* Download Template Button */}
              <div className="text-center">
                <button
                  onClick={() => onDownloadTemplate()}
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                >
                  <Download className="w-5 h-5" />
                  <span>Download CSV Template</span>
                </button>
                <p className="text-xs text-gray-500 mt-2">{templateFilename}</p>
              </div>

              {/* Custom Instructions */}
              {instructions && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  {instructions}
                </div>
              )}

              {/* Upload Area */}
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg font-semibold text-gray-700 mb-2">
                    Click to upload CSV file
                  </p>
                  <p className="text-sm text-gray-500">
                    or drag and drop your CSV file here
                  </p>
                </label>
              </div>
            </div>
          )}

          {/* Step 2: Preview & Validate */}
          {step === 2 && (
            <div className="space-y-6">
              {/* Validation Messages */}
              {validation?.errors && validation.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-red-900 mb-2">Errors Found:</p>
                      <ul className="text-sm text-red-800 space-y-1">
                        {validation.errors.map((error, i) => (
                          <li key={i}>• {error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {validation?.warnings && validation.warnings.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-yellow-900 mb-2">Warnings:</p>
                      <ul className="text-sm text-yellow-800 space-y-1">
                        {validation.warnings.map((warning, i) => (
                          <li key={i}>• {warning}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {validation?.valid && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <p className="font-semibold text-green-900">
                      ✓ Validation passed! Ready to import {parsedData?.length || 0} {type}.
                    </p>
                  </div>
                </div>
              )}

              {/* Data Preview */}
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <Eye className="w-5 h-5 text-gray-600" />
                  <h4 className="font-semibold text-gray-900">Data Preview</h4>
                  <span className="text-sm text-gray-500">({parsedData?.length || 0} rows)</span>
                </div>

                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto max-h-96">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          {parsedData && parsedData[0] && Object.keys(parsedData[0]).map((key, i) => (
                            <th key={i} className="px-4 py-2 text-left font-semibold text-gray-700">
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {parsedData && parsedData.slice(0, 10).map((row, i) => (
                          <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                            {Object.values(row).map((value, j) => (
                              <td key={j} className="px-4 py-2 text-gray-900">
                                {value}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {parsedData && parsedData.length > 10 && (
                    <div className="px-4 py-2 bg-gray-50 text-xs text-gray-500 text-center">
                      Showing 10 of {parsedData.length} rows
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={() => setStep(1)}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                >
                  Back
                </button>
                <button
                  onClick={handleImport}
                  disabled={!validation?.valid}
                  className={`flex-1 py-3 rounded-lg font-semibold text-white transition-colors ${
                    validation?.valid
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  Import {parsedData?.length || 0} {type}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Importing/Result */}
          {step === 3 && (
            <div className="text-center py-12">
              {importing ? (
                <>
                  <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-lg font-semibold text-gray-900">Importing {type}...</p>
                  <p className="text-sm text-gray-600 mt-2">This may take a moment</p>
                </>
              ) : importResult?.success ? (
                <>
                  <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                  <p className="text-lg font-semibold text-gray-900 mb-2">
                    Import Successful!
                  </p>
                  <p className="text-gray-600 mb-6">
                    {importResult.message || `Successfully imported ${importResult.count || parsedData?.length || 0} ${type}`}
                  </p>
                  <button
                    onClick={handleClose}
                    className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors font-semibold"
                  >
                    Done
                  </button>
                </>
              ) : (
                <>
                  <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
                  <p className="text-lg font-semibold text-gray-900 mb-2">
                    Import Failed
                  </p>
                  <p className="text-gray-600 mb-6">
                    {importResult?.message || 'An error occurred during import'}
                  </p>
                  <div className="flex space-x-3 justify-center">
                    <button
                      onClick={() => setStep(2)}
                      className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                    >
                      Try Again
                    </button>
                    <button
                      onClick={handleClose}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CSVImportModal;
